import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { API_BASE_URL } from '../../config/apiConfig';
import { AdminLayout } from './components/AdminLayout';
import axios from "axios";

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  margin: 0;
  color: #2c3e50;
  font-size: 28px;
`;

const TableContainer = styled.div`
  max-height: calc(100vh - 280px);
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  
  thead {
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  tbody tr:nth-child(even) {
    background-color: #fafafa;
  }
  
  tbody tr:hover {
    background-color: #f5faff;
  }
`;

const Th = styled.th`
  background-color: #f1f1f1;
  padding: 8px;
  text-align: left;
  border: 1px solid #ddd;
`;

const Td = styled.td`
  padding: 6px 8px;
  border: 1px solid #ddd;
  vertical-align: middle;
`;

const ErrorMessage = styled.p`
  color: red;
  text-align: center;
`;

const LoadingMessage = styled.p`
  text-align: center;
  font-size: 18px;
  margin-top: 20px;
`;

const FilterContainer = styled.div`
  margin-bottom: 20px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const FilterSection = styled.div`
  margin-bottom: 16px;
`;

const FilterTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 8px;
  font-size: 14px;
  color: #555;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 16px;
  max-height: ${props => props.$collapsed ? '0' : '200px'};
  overflow-y: auto;
  overflow-x: hidden;
  transition: max-height 0.3s ease;
  padding: ${props => props.$collapsed ? '0' : '8px'};
  background-color: ${props => props.$collapsed ? 'transparent' : '#fafafa'};
  border-radius: 8px;
  border: ${props => props.$collapsed ? 'none' : '1px solid #e0e0e0'};
`;

const ItemsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const GroupLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  padding: 4px 0;
  border-bottom: 1px solid #e0e0e0;
  margin-top: 4px;
  
  &:first-child {
    margin-top: 0;
  }
`;

const GroupItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const FilterSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 8px 0;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ExpandIcon = styled.span`
  font-size: 12px;
  color: #666;
  transition: transform 0.3s ease;
  transform: ${props => props.$expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const Tag = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background-color: ${props => props.active ? '#4caf50' : '#f1f1f1'};
  color: ${props => props.active ? 'white' : 'black'};
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.active ? '#3d8b40' : '#e0e0e0'};
  }
`;

const TagCount = styled.span`
  background-color: ${props => props.active ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.1)'};
  border-radius: 10px;
  padding: 2px 6px;
  margin-left: 6px;
  font-size: 11px;
`;

const SelectedChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  background-color: #e8f0fe;
  color: #1a73e8;
  border: 1px solid #c7d2fe;
  border-radius: 16px;
  padding: 4px 8px;
  font-size: 12px;
`;

const ChipRemove = styled.button`
  margin-left: 6px;
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  font-size: 12px;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const SmallButton = styled.button`
  padding: 6px 10px;
  border: 1px solid #e0e0e0;
  background: #fff;
  color: #2c3e50;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  
  &:hover {
    background-color: #f7f7f7;
    border-color: #d5d5d5;
  }
`;

const Avatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
`;

const DefaultAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #757575;
  font-size: 11px;
`;

const GroupHeader = styled.th`
  background-color: #e0e0e0;
  padding: 10px;
  text-align: center;
  border: 1px solid #ddd;
`;

const SortableHeader = styled(Th)`
  cursor: pointer;
  position: relative;
  
  &:hover {
    background-color: #e8e8e8;
  }
  
  &::after {
    content: '${props => props.sortDirection === 'asc' ? ' ▲' : props.sortDirection === 'desc' ? ' ▼' : ''}';
  }
`;

const Select = styled.select`
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-width: 200px;
`;

const FilterInput = styled.input`
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-width: 200px;
  font-size: 13px;
`;

const WaiverToggle = styled.button`
  padding: 4px 8px;
  border: none;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 60px;
  
  ${props => props.$waived ? `
    background-color: #4caf50;
    color: white;
    &:hover {
      background-color: #388e3c;
    }
  ` : `
    background-color: #e0e0e0;
    color: #666;
    &:hover {
      background-color: #bdbdbd;
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusBadge = styled.span`
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => {
    switch (props.$status?.toLowerCase()) {
      case 'active':
        return `
          background-color: #d4edda;
          color: #155724;
        `;
      case 'pending':
        return `
          background-color: #fff3cd;
          color: #856404;
        `;
      case 'expired':
      case 'cancelled':
        return `
          background-color: #f8d7da;
          color: #721c24;
        `;
      default:
        return `
          background-color: #e9ecef;
          color: #6c757d;
        `;
    }
  }}
`;

const WaiverInfo = styled.div`
  font-size: 10px;
  color: #888;
  margin-top: 2px;
`;

export function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    name: '',
    email: '',
    profession: '',
    subscriptionType: '',
    companyName: '',
    pincodes: [],
    needItems: [],
    greedItems: []
  });
  const [needSearchQuery, setNeedSearchQuery] = useState('');
  const [greedSearchQuery, setGreedSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    pincodes: false,
    needItems: false,
    greedItems: false
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'id',
    direction: 'asc'
  });
  const [updatingWaiver, setUpdatingWaiver] = useState({});
  
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchUsers = async () => {
      const token = sessionStorage.getItem('admin-token');
      
      if (!token) {
        navigate('/admin/login');
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/admin/users`, {
          headers: {
            'Authorization': token,
          },
        });
        console.log('response:', response)
        const data = await response.data;
        console.log('data:', data)
        if (!data.is_success) {
          throw new Error(data.message || 'Failed to fetch users');
        }
        
        setUsers(data.users);
      } catch (err) {
        setError(err.message || 'An error occurred while fetching users');
        if (err.message === 'Unauthorized') {
          sessionStorage.removeItem('admin-token');
          navigate('/admin/login');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchUsers();
  }, [navigate]);

  const handleToggleWaiver = async (userId, currentWaived) => {
    const token = sessionStorage.getItem('admin-token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    // Confirm action
    const action = currentWaived ? 'remove waiver from' : 'waive subscription fees for';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    setUpdatingWaiver(prev => ({ ...prev, [userId]: true }));

    try {
      const response = await axios.put(
        `${API_BASE_URL}/admin/users/${userId}/waiver`,
        { waived: !currentWaived },
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.is_success) {
        // Update the user in the local state
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user.userId === userId 
              ? { 
                  ...user, 
                  subscriptionWaived: response.data.user.subscriptionWaived,
                  subscriptionWaivedAt: response.data.user.subscriptionWaivedAt,
                  subscriptionWaivedBy: response.data.user.subscriptionWaivedBy,
                  ...(response.data.subscriptionUpdate ? response.data.subscriptionUpdate : {})
                }
              : user
          )
        );
      } else {
        throw new Error(response.data.message || 'Failed to update waiver');
      }
    } catch (err) {
      console.error('Error toggling waiver:', err);
      alert(err.response?.data?.message || err.message || 'Failed to update waiver');
    } finally {
      setUpdatingWaiver(prev => ({ ...prev, [userId]: false }));
    }
  };

  const formatWaiverDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString();
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleTagFilter = (filterType, value) => {
    setFilters(prev => {
      const currentValues = [...prev[filterType]];
      const index = currentValues.indexOf(value);
      
      if (index === -1) {
        // Add the value if not present
        return { ...prev, [filterType]: [...currentValues, value] };
      } else {
        // Remove the value if already present
        currentValues.splice(index, 1);
        return { ...prev, [filterType]: currentValues };
      }
    });
  };
  
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  const calculateAge = (dob) => {
    if (!dob) return '-';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age > 0 ? age : '-';
  };
  
  const getInitials = (user) => {
    if (!user.firstname && !user.lastname) return '?';
    return `${user.firstname ? user.firstname[0] : ''}${user.lastname ? user.lastname[0] : ''}`;
  };
  
  // Extract list items
  const extractItems = (list) => {
    if (!list) return [];
    return list.split(',').map(item => item.trim()).filter(item => item);
  };
  
  // Basic fuzzy matching util: includes OR each token is a subsequence
  const isSubsequence = (pattern, text) => {
    if (!pattern) return true;
    let p = 0;
    const pat = pattern.toLowerCase();
    const t = text.toLowerCase();
    for (let i = 0; i < t.length && p < pat.length; i++) {
      if (t[i] === pat[p]) p++;
    }
    return p === pat.length;
  };
  
  const fuzzyMatch = (query, target) => {
    if (!query) return true;
    const q = query.trim().toLowerCase();
    const t = target.toLowerCase();
    if (t.includes(q)) return true;
    const tokens = q.split(/\s+/).filter(Boolean);
    return tokens.every(tok => isSubsequence(tok, t));
  };
  
  // Get all unique items across all users (case-insensitive, keeps most common casing)
  const getUniqueItemsWithCounts = (listField) => {
    const itemMap = {}; // lowercase -> { displayName, count }
    users.forEach(user => {
      if (user[listField]) {
        extractItems(user[listField]).forEach(item => {
          const key = item.toLowerCase();
          if (!itemMap[key]) {
            itemMap[key] = { displayName: item, count: 1, variants: { [item]: 1 } };
          } else {
            itemMap[key].count++;
            itemMap[key].variants[item] = (itemMap[key].variants[item] || 0) + 1;
            // Use the most common variant as display name
            const maxVariant = Object.entries(itemMap[key].variants)
              .sort((a, b) => b[1] - a[1])[0][0];
            itemMap[key].displayName = maxVariant;
          }
        });
      }
    });
    return itemMap;
  };

  const needItemsMap = getUniqueItemsWithCounts('needList');
  const greedItemsMap = getUniqueItemsWithCounts('greedList');
  
  // Get display names and counts
  const getAllNeedItems = () => Object.values(needItemsMap).map(v => v.displayName);
  const getAllGreedItems = () => Object.values(greedItemsMap).map(v => v.displayName);
  
  const needItemCounts = Object.fromEntries(
    Object.values(needItemsMap).map(v => [v.displayName, v.count])
  );
  const greedItemCounts = Object.fromEntries(
    Object.values(greedItemsMap).map(v => [v.displayName, v.count])
  );
  
  // Compute displayed tag lists with fuzzy search and useful sorting
  const displayedNeedItems = getAllNeedItems()
    .filter(item => fuzzyMatch(needSearchQuery, item))
    .sort((a, b) => (needItemCounts[b] || 0) - (needItemCounts[a] || 0) || a.localeCompare(b));
  
  const displayedGreedItems = getAllGreedItems()
    .filter(item => fuzzyMatch(greedSearchQuery, item))
    .sort((a, b) => (greedItemCounts[b] || 0) - (greedItemCounts[a] || 0) || a.localeCompare(b));
  
  const filteredUsers = users.filter(user => {
    const fullName = `${user.firstname || ''} ${user.lastname || ''}`.toLowerCase();
    const userNeedItems = extractItems(user.needList).map(i => i.toLowerCase());
    const userGreedItems = extractItems(user.greedList).map(i => i.toLowerCase());
    
    // Check if user has any of the filtered need items (case-insensitive)
    const needItemsMatch = filters.needItems.length === 0 || 
      filters.needItems.some(item => userNeedItems.includes(item.toLowerCase()));
      
    // Check if user has any of the filtered greed items (case-insensitive)
    const greedItemsMatch = filters.greedItems.length === 0 || 
      filters.greedItems.some(item => userGreedItems.includes(item.toLowerCase()));
      
    // Check if user's pincode is in filtered pincodes
    const pincodeMatch = filters.pincodes.length === 0 || 
      (user.pincode && filters.pincodes.includes(user.pincode.toString()));
    
    // Check company name filter
    const companyNameMatch = filters.companyName === '' || 
      (user.companyName && user.companyName.toLowerCase().includes(filters.companyName.toLowerCase()));
    
    return (
      (filters.name === '' || fullName.includes(filters.name.toLowerCase())) &&
      (filters.email === '' || (user.email && user.email.toLowerCase().includes(filters.email.toLowerCase()))) &&
      (filters.profession === '' || user.profession === filters.profession) &&
      (filters.subscriptionType === '' || user.subscriptionType === filters.subscriptionType) &&
      companyNameMatch && pincodeMatch && needItemsMatch && greedItemsMatch
    );
  });
  
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (sortConfig.key === 'subscriptionWaivedAmt') {
      const discountA = Number(a.subscriptionWaivedAmt || 0);
      const discountB = Number(b.subscriptionWaivedAmt || 0);
      if (discountA < discountB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (discountA > discountB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    }

    if (!a[sortConfig.key] && !b[sortConfig.key]) return 0;
    if (!a[sortConfig.key]) return 1;
    if (!b[sortConfig.key]) return -1;
    
    const valA = typeof a[sortConfig.key] === 'string' ? a[sortConfig.key].toLowerCase() : a[sortConfig.key];
    const valB = typeof b[sortConfig.key] === 'string' ? b[sortConfig.key].toLowerCase() : b[sortConfig.key];
    
    if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
    if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });
  
  const uniqueProfessions = [...new Set(users.filter(user => user.profession).map(user => user.profession))];
  const uniqueSubscriptionTypes = [...new Set(users.filter(user => user.subscriptionType).map(user => user.subscriptionType))];
  const uniquePincodes = [...new Set(users.filter(user => user.pincode).map(user => user.pincode.toString()))];
  
  // Count occurrences of each pincode
  const pincodeCounts = {};
  users.forEach(user => {
    if (user.pincode) {
      const pincode = user.pincode.toString();
      pincodeCounts[pincode] = (pincodeCounts[pincode] || 0) + 1;
    }
  });
  
  const clearSelected = (type) => {
    setFilters(prev => ({ ...prev, [type]: [] }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Group items by first letter
  const groupByFirstLetter = (items, counts, activeItems = []) => {
    const groups = {};
    items.forEach(item => {
      const firstChar = item.charAt(0).toUpperCase();
      const key = /[A-Z]/.test(firstChar) ? firstChar : '#';
      if (!groups[key]) groups[key] = [];
      groups[key].push({ name: item, count: counts[item] || 0, active: activeItems.includes(item) });
    });
    // Sort each group by count descending
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => b.count - a.count);
    });
    return groups;
  };
  
  // Date/Time formatters
  const formatDateTime = (ts) => {
    if (!ts) return '-';
    const date = new Date(ts);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString();
  };

  const formatDateISO = (ts) => {
    if (!ts) return '';
    const date = new Date(ts);
    if (isNaN(date.getTime())) return '';
    return date.toISOString();
  };

  // CSV helpers
  const escapeCSVValue = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value).replace(/\r?\n|\r/g, ' ').trim();
    if (/[",\n]/.test(str)) {
      return '"' + str.replace(/"/g, '""') + '"';
    }
    return str;
  };

  const buildUsersCSV = (rows) => {
    const headers = [
      'User ID',
      'Name',
      'Email',
      'GST',
      'Mobile',
      'Age',
      'Gender',
      'Profession',
      'Shipping Address',
      'Billing Address',
      'Pincode',
      'Subscription Type',
      'Subscription Discount Percent',
      'Need List',
      'Greed List'
      , 'Created At'
    ];

    const dataLines = rows.map(user => {
      const line = [
        user.userId || '',
        `${user.firstname || ''} ${user.lastname || ''}`.trim(),
        user.email || '',
        user.gst || '',
        user.mobile || '',
        calculateAge(user.dob) || '-',
        user.gender || '-',
        user.profession || '-',
        user.address || '-',
        user.professionAddress || '-',
        user.pincode || '-',
        user.subscriptionType || '-',
        user.subscriptionWaivedAmt ? Number(user.subscriptionWaivedAmt) : 0,
        user.needList || '',
        user.greedList || '',
        formatDateISO(user.createdAt)
      ];
      return line.map(escapeCSVValue).join(',');
    });

    return [headers.map(escapeCSVValue).join(','), ...dataLines].join('\n');
  };

  const handleDownloadCSV = () => {
    const csvContent = buildUsersCSV(sortedUsers);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const date = new Date();
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    link.setAttribute('href', url);
    link.setAttribute('download', `users_${y}-${m}-${d}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return <LoadingMessage>Loading users...</LoadingMessage>;
  }
  
  return (
    <AdminLayout>
      <Header>
        <Title>User Management</Title>
        <SmallButton onClick={handleDownloadCSV}>Download CSV</SmallButton>
      </Header>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <FilterContainer>
        <FilterInput 
          placeholder="Filter by name" 
          name="name" 
          value={filters.name} 
          onChange={handleFilterChange}
        />
        <FilterInput 
          placeholder="Filter by email" 
          name="email" 
          value={filters.email} 
          onChange={handleFilterChange}
        />
        <Select 
          name="profession" 
          value={filters.profession} 
          onChange={handleFilterChange}
        >
          <option value="">All professions</option>
          {uniqueProfessions.map(profession => (
            <option key={profession} value={profession}>{profession}</option>
          ))}
        </Select>
        <Select 
          name="subscriptionType" 
          value={filters.subscriptionType} 
          onChange={handleFilterChange}
        >
          <option value="">All subscription types</option>
          {uniqueSubscriptionTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </Select>
        <FilterInput 
          placeholder="Filter by company name" 
          name="companyName" 
          value={filters.companyName} 
          onChange={handleFilterChange}
        />
      </FilterContainer>
      
      {/* Pincode Filters */}
      <FilterSection>
        <FilterSectionHeader onClick={() => toggleSection('pincodes')}>
          <FilterTitle style={{ margin: 0 }}>
            Filter by Pincode {filters.pincodes.length > 0 && `(${filters.pincodes.length} selected)`}
          </FilterTitle>
          <ExpandIcon $expanded={expandedSections.pincodes}>▼</ExpandIcon>
        </FilterSectionHeader>
        {filters.pincodes.length > 0 && (
          <SelectedChips>
            {filters.pincodes.map(pincode => (
              <Chip key={pincode}>
                {pincode}
                <ChipRemove onClick={() => handleTagFilter('pincodes', pincode)}>×</ChipRemove>
              </Chip>
            ))}
            <SmallButton onClick={() => clearSelected('pincodes')}>Clear</SmallButton>
          </SelectedChips>
        )}
        <TagsContainer $collapsed={!expandedSections.pincodes}>
          <GroupItems>
            {uniquePincodes.sort((a, b) => (pincodeCounts[b] || 0) - (pincodeCounts[a] || 0)).map(pincode => (
              <Tag 
                key={pincode} 
                active={filters.pincodes.includes(pincode)}
                onClick={() => handleTagFilter('pincodes', pincode)}
              >
                {pincode}
                <TagCount active={filters.pincodes.includes(pincode)}>
                  {pincodeCounts[pincode]}
                </TagCount>
              </Tag>
            ))}
          </GroupItems>
        </TagsContainer>
      </FilterSection>
      
      {/* Need Items Filters */}
      <FilterSection>
        <FilterSectionHeader onClick={() => toggleSection('needItems')}>
          <FilterTitle style={{ margin: 0 }}>
            Filter by Need Items {filters.needItems.length > 0 && `(${filters.needItems.length} selected)`}
          </FilterTitle>
          <ExpandIcon $expanded={expandedSections.needItems}>▼</ExpandIcon>
        </FilterSectionHeader>
        {filters.needItems.length > 0 && (
          <SelectedChips>
            {filters.needItems.map(item => (
              <Chip key={item}>
                {item}
                <ChipRemove onClick={() => handleTagFilter('needItems', item)}>×</ChipRemove>
              </Chip>
            ))}
            <SmallButton onClick={() => clearSelected('needItems')}>Clear</SmallButton>
          </SelectedChips>
        )}
        {expandedSections.needItems && (
          <FilterRow>
            <FilterInput 
              placeholder="Search need items"
              value={needSearchQuery}
              onChange={(e) => setNeedSearchQuery(e.target.value)}
            />
          </FilterRow>
        )}
        <TagsContainer $collapsed={!expandedSections.needItems}>
          <ItemsGroup>
            {Object.entries(groupByFirstLetter(displayedNeedItems, needItemCounts, filters.needItems))
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([letter, items]) => (
                <div key={letter}>
                  <GroupLabel>{letter}</GroupLabel>
                  <GroupItems>
                    {items.map(item => (
                      <Tag 
                        key={item.name} 
                        active={item.active}
                        onClick={() => handleTagFilter('needItems', item.name)}
                      >
                        {item.name}
                        <TagCount active={item.active}>
                          {item.count}
                        </TagCount>
                      </Tag>
                    ))}
                  </GroupItems>
                </div>
              ))}
          </ItemsGroup>
        </TagsContainer>
      </FilterSection>
      
      {/* Greed Items Filters */}
      <FilterSection>
        <FilterSectionHeader onClick={() => toggleSection('greedItems')}>
          <FilterTitle style={{ margin: 0 }}>
            Filter by Greed Items {filters.greedItems.length > 0 && `(${filters.greedItems.length} selected)`}
          </FilterTitle>
          <ExpandIcon $expanded={expandedSections.greedItems}>▼</ExpandIcon>
        </FilterSectionHeader>
        {filters.greedItems.length > 0 && (
          <SelectedChips>
            {filters.greedItems.map(item => (
              <Chip key={item}>
                {item}
                <ChipRemove onClick={() => handleTagFilter('greedItems', item)}>×</ChipRemove>
              </Chip>
            ))}
            <SmallButton onClick={() => clearSelected('greedItems')}>Clear</SmallButton>
          </SelectedChips>
        )}
        {expandedSections.greedItems && (
          <FilterRow>
            <FilterInput 
              placeholder="Search greed items"
              value={greedSearchQuery}
              onChange={(e) => setGreedSearchQuery(e.target.value)}
            />
          </FilterRow>
        )}
        <TagsContainer $collapsed={!expandedSections.greedItems}>
          <ItemsGroup>
            {Object.entries(groupByFirstLetter(displayedGreedItems, greedItemCounts, filters.greedItems))
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([letter, items]) => (
                <div key={letter}>
                  <GroupLabel>{letter}</GroupLabel>
                  <GroupItems>
                    {items.map(item => (
                      <Tag 
                        key={item.name} 
                        active={item.active}
                        onClick={() => handleTagFilter('greedItems', item.name)}
                      >
                        {item.name}
                        <TagCount active={item.active}>
                          {item.count}
                        </TagCount>
                      </Tag>
                    ))}
                  </GroupItems>
                </div>
              ))}
          </ItemsGroup>
        </TagsContainer>
      </FilterSection>
      
      <TableContainer>
      <Table>
        <thead>
          <tr>
            <GroupHeader colSpan="5">User Identification</GroupHeader>
            <GroupHeader colSpan="5">Personal Details</GroupHeader>
            <GroupHeader colSpan="3">Shipping Details</GroupHeader>
            <GroupHeader colSpan="6">Subscription</GroupHeader>
            <GroupHeader colSpan="1">Meta</GroupHeader>
          </tr>
          <tr>
            <SortableHeader onClick={() => handleSort('userId')} sortDirection={sortConfig.key === 'userId' ? sortConfig.direction : ''}>User ID</SortableHeader>
            <SortableHeader onClick={() => handleSort('firstname')} sortDirection={sortConfig.key === 'firstname' ? sortConfig.direction : ''}>Name</SortableHeader>
            <SortableHeader onClick={() => handleSort('email')} sortDirection={sortConfig.key === 'email' ? sortConfig.direction : ''}>Email</SortableHeader>
            <SortableHeader onClick={() => handleSort('gst')} sortDirection={sortConfig.key === 'gst' ? sortConfig.direction : ''}>GST</SortableHeader>
            <SortableHeader onClick={() => handleSort('companyName')} sortDirection={sortConfig.key === 'companyName' ? sortConfig.direction : ''}>Company</SortableHeader>
            
            <Th>Avatar</Th>
            <SortableHeader onClick={() => handleSort('mobile')} sortDirection={sortConfig.key === 'mobile' ? sortConfig.direction : ''}>Mobile</SortableHeader>
            <SortableHeader onClick={() => handleSort('dob')} sortDirection={sortConfig.key === 'dob' ? sortConfig.direction : ''}>Age</SortableHeader>
            <SortableHeader onClick={() => handleSort('gender')} sortDirection={sortConfig.key === 'gender' ? sortConfig.direction : ''}>Gender</SortableHeader>
            <SortableHeader onClick={() => handleSort('profession')} sortDirection={sortConfig.key === 'profession' ? sortConfig.direction : ''}>Profession</SortableHeader>
            
            <SortableHeader onClick={() => handleSort('address')} sortDirection={sortConfig.key === 'address' ? sortConfig.direction : ''}>Shipping Address</SortableHeader>
            <SortableHeader onClick={() => handleSort('professionAddress')} sortDirection={sortConfig.key === 'professionAddress' ? sortConfig.direction : ''}>Billing Address</SortableHeader>
            <SortableHeader onClick={() => handleSort('pincode')} sortDirection={sortConfig.key === 'pincode' ? sortConfig.direction : ''}>Pincode</SortableHeader>
            
            <SortableHeader onClick={() => handleSort('subscriptionType')} sortDirection={sortConfig.key === 'subscriptionType' ? sortConfig.direction : ''}>Type</SortableHeader>
            <SortableHeader onClick={() => handleSort('needList')} sortDirection={sortConfig.key === 'needList' ? sortConfig.direction : ''}>Need List</SortableHeader>
            <SortableHeader onClick={() => handleSort('greedList')} sortDirection={sortConfig.key === 'greedList' ? sortConfig.direction : ''}>Greed List</SortableHeader>
            <SortableHeader onClick={() => handleSort('subscriptionStatus')} sortDirection={sortConfig.key === 'subscriptionStatus' ? sortConfig.direction : ''}>Status</SortableHeader>
            <SortableHeader onClick={() => handleSort('subscriptionWaivedAmt')} sortDirection={sortConfig.key === 'subscriptionWaivedAmt' ? sortConfig.direction : ''}>Disc %</SortableHeader>
            <SortableHeader onClick={() => handleSort('subscriptionWaived')} sortDirection={sortConfig.key === 'subscriptionWaived' ? sortConfig.direction : ''}>Waived</SortableHeader>
            <SortableHeader onClick={() => handleSort('createdAt')} sortDirection={sortConfig.key === 'createdAt' ? sortConfig.direction : ''}>Created At</SortableHeader>
          </tr>
        </thead>
        <tbody>
          {sortedUsers.length > 0 ? (
            sortedUsers.map(user => (
              <tr key={user.id}>
                <Td>{user.userId}</Td>
                <Td>{`${user.firstname || ''} ${user.lastname || ''}`}</Td>
                <Td>{user.email}</Td>
                <Td>{user.gst || '-'}</Td>
                <Td>{user.companyName || '-'}</Td>
                
                <Td>
                  {user.profile_image ? (
                    <Avatar src={user.profile_image} alt={`${user.firstname}'s avatar`} />
                  ) : (
                    <DefaultAvatar>{getInitials(user)}</DefaultAvatar>
                  )}
                </Td>
                <Td>{user.mobile || '-'}</Td>
                <Td>{calculateAge(user.dob)}</Td>
                <Td>{user.gender || '-'}</Td>
                <Td>{user.profession || '-'}</Td>
                
                <Td>{user.address || '-'}</Td>
                <Td>{user.professionAddress ? user.professionAddress.substring(0, 20) + (user.professionAddress.length > 20 ? '...' : '') : '-'}</Td>
                <Td>{user.pincode || '-'}</Td>
                
                <Td>{user.subscriptionType || '-'}</Td>
                <Td>{user.needList ? user.needList : '-'}</Td>
                <Td>{user.greedList ? user.greedList : '-'}</Td>
                <Td>
                  {user.subscriptionStatus ? (
                    <StatusBadge $status={user.subscriptionStatus}>
                      {user.subscriptionStatus}
                    </StatusBadge>
                  ) : '-'}
                </Td>
                <Td>
                  {Number(user.subscriptionWaivedAmt || 0) > 0
                    ? `${Number(user.subscriptionWaivedAmt)}%`
                    : '-'}
                </Td>
                <Td>
                  {(() => {
                    const amountNum = Number(user.subscriptionAmount);
                    const isActive = Boolean(user.subscriptionActive);
                    const status = (user.subscriptionStatus || '').toLowerCase();
                    const feeWaived = Boolean(user.subscriptionWaived) || (isActive && status === 'active' && Number.isFinite(amountNum) && amountNum === 0);

                    return (
                      <>
                        <WaiverToggle
                          $waived={feeWaived}
                          onClick={() => handleToggleWaiver(user.userId, feeWaived)}
                          disabled={updatingWaiver[user.userId] || (isActive && !feeWaived)}
                          title={feeWaived
                            ? `Fee waived${user.subscriptionWaivedBy ? ` by ${user.subscriptionWaivedBy}` : ''}${user.subscriptionWaivedAt ? ` on ${formatWaiverDate(user.subscriptionWaivedAt)}` : ''}`
                            : (isActive ? `Subscription active (${user.subscriptionStatus}) - paid` : 'Click to waive subscription fees')
                          }
                        >
                          {updatingWaiver[user.userId]
                            ? '...'
                            : (feeWaived ? 'Waived' : (isActive ? 'Paid' : 'No'))}
                        </WaiverToggle>
                        {Boolean(user.subscriptionWaived) && user.subscriptionWaivedAt && (
                          <WaiverInfo>{formatWaiverDate(user.subscriptionWaivedAt)}</WaiverInfo>
                        )}
                      </>
                    );
                  })()}
                </Td>
                <Td>{formatDateTime(user.createdAt)}</Td>
              </tr>
            ))
          ) : (
            <tr>
              <Td colSpan="20" style={{ textAlign: 'center' }}>No users found</Td>
            </tr>
          )}
        </tbody>
      </Table>
      </TableContainer>
    </AdminLayout>
  );
} 